import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { useGetInsightsLabQuery } from 'api/insightsLab'
import PageHeader from 'components/PageHeader'
import SearchablePaginatedContent from 'components/SearchablePaginatedContent'
import SideNav from 'components/SideNav'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import usePaginateArray from 'hooks/usePaginateArray'
import { buildTranslate } from 'locales'
import { useMemo, useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap-five'
import InsightsLabBanner from './InsightsLabBanner'
import ReportItem from './ReportItem'

const t = buildTranslate('insights_lab')

const InsightsLab = () => {
  const [css] = useStyletron()
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const merchant = useCurrentMerchant()

  const { data: insightsLab = [], isLoading } = useGetInsightsLabQuery(
    undefined,
    {
      skip: !merchant, // Skip the query if there's no merchant
    }
  )

  // Get all unique categories with counts
  const categories = useMemo(() => {
    return Array.from(
      new Set(insightsLab.map(item => item.category)).values()
    ).sort((a, b) => a.localeCompare(b))
  }, [insightsLab])

  const filteredInsights = useMemo(() => {
    let results = [...insightsLab]

    // Apply category filter
    if (
      selectedCategory &&
      selectedCategory !== 'all' &&
      searchQuery.trim() === ''
    ) {
      results = results.filter(item => item.category === selectedCategory)
    }

    // Apply search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      results = results.filter(
        item =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    return results
  }, [insightsLab, selectedCategory, searchQuery])

  const { currentPage, setPage, paginatedData, totalPages } = usePaginateArray(
    filteredInsights,
    15
  )

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setPage(1)
  }

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category)
    setPage(1)
  }

  if (!merchant) return null

  return (
    <PageHeader title={t('view.title')} description={t('view.subtitle')}>
      <div
        className={`w-100 grey-05-bkg border-top-1 grey-20-border ${css({
          minHeight: '100vh',
        })}`}
      >
        <Container className="full-height pb-xl">
          <InsightsLabBanner />
          <SearchablePaginatedContent
            contentIsLoading={isLoading}
            onChangePage={setPage}
            onChangeSearchInput={handleSearchChange}
            currentPage={currentPage}
            numberOfPages={totalPages}
          >
            <Row className="mt-xl" style={{ scrollMarginTop: '75px' }}>
              <Switch condition={searchQuery.trim() === ''}>
                <Col data-testid="category-filter" md={3} className="pr-l">
                  <SideNav
                    items={categories}
                    current={selectedCategory || ''}
                    all={t('categories.all')}
                    onClick={handleCategorySelect}
                  />
                </Col>
              </Switch>
              <Col>
                <Switch condition={searchQuery.trim() === ''}>
                  <Row>
                    <Col>
                      <Text.Header3 className="mt-0 mb-s">
                        {selectedCategory === 'all'
                          ? t('categories.all')
                          : selectedCategory || t('other')}
                      </Text.Header3>
                    </Col>
                  </Row>
                </Switch>

                {paginatedData.map(item => (
                  <ReportItem key={item.id} {...item} />
                ))}
              </Col>
            </Row>
          </SearchablePaginatedContent>
        </Container>
      </div>
    </PageHeader>
  )
}

export default InsightsLab
